import type { PortalAssetAiField, PortalAssetEnrichmentSuggestion } from '../../types/portalAsset';
import { normalizeAssetTitle } from './derived';

// THE ENRICHMENT PANEL'S DECISIONS, PURE (Run D). No React, no network: what the Accept button sends, which fields
// count as "left exactly as suggested", and the one sentence per state. Kept out of the component so the rules are
// asserted without jsdom (the suite sits close to its timeout cliff — CLAUDE.md "jsdom timeout cliff").

export type EnrichDrafts = { title: string; tags: readonly string[]; categoryIds: readonly string[] };

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');

/** The fields the suggestion proposed (a field the wall protected is NOT in this list). */
export function proposedFields(s: PortalAssetEnrichmentSuggestion): PortalAssetAiField[] {
  const out: PortalAssetAiField[] = [];
  if (s.title !== null) out.push('title');
  if (s.tags !== null) out.push('tags');
  if (s.categories !== null) out.push('category');
  return out;
}

/** The drafts the form should show the moment a suggestion lands: proposed fields replaced, the rest untouched. */
export function applySuggestion(drafts: EnrichDrafts, s: PortalAssetEnrichmentSuggestion): EnrichDrafts {
  return {
    title: s.title ?? drafts.title,
    tags: s.tags ?? drafts.tags,
    categoryIds: s.categories ? s.categories.map((c) => c.id) : drafts.categoryIds,
  };
}

/**
 * Which proposed fields the user left EXACTLY as suggested. ⚠️ A claim, not a fact: the server re-checks each against
 * the receipt and believes it only where the saved value equals the receipted one. Editing the title does not make
 * the tags human — it is field by field.
 */
export function untouchedFields(s: PortalAssetEnrichmentSuggestion, d: EnrichDrafts): PortalAssetAiField[] {
  const out: PortalAssetAiField[] = [];
  if (s.title !== null) {
    const n = normalizeAssetTitle(d.title);
    if (n.ok && n.text === s.title) out.push('title');
  }
  if (s.tags !== null && d.tags.join('\u0000') === s.tags.join('\u0000')) out.push('tags');
  if (s.categories !== null && sameSet(d.categoryIds, s.categories.map((c) => c.id))) out.push('category');
  return out;
}

export type AcceptPatch = {
  title?: string | null;
  tags?: string[];
  categoryIds?: string[];
  aiSuggestion: { receipt: string; untouched: PortalAssetAiField[] };
};

/**
 * The ONE patch Accept sends: only the fields the suggestion proposed, carrying the user's CURRENT drafts (so an
 * edit is honoured), plus the receipt. null = the title draft is invalid (Accept must be disabled).
 */
export function acceptPatch(s: PortalAssetEnrichmentSuggestion, d: EnrichDrafts): AcceptPatch | null {
  const patch: AcceptPatch = { aiSuggestion: { receipt: s.receipt, untouched: untouchedFields(s, d) } };
  if (s.title !== null) {
    const n = normalizeAssetTitle(d.title);
    if (!n.ok) return null;
    patch.title = n.text;
  }
  if (s.tags !== null) patch.tags = [...d.tags];
  if (s.categories !== null) patch.categoryIds = [...d.categoryIds];
  return patch;
}

const FIELD_WORD: Record<PortalAssetAiField, string> = { title: 'title', tags: 'tags', category: 'category' };

/** "Kept the title and tags you set." — null when nothing was held back. */
export function keptNote(s: PortalAssetEnrichmentSuggestion): string | null {
  if (s.kept.length === 0) return null;
  return `Kept the ${s.kept.map((f) => FIELD_WORD[f]).join(' and ')} a person already set.`;
}

/** Whether the button may be pressed. ⚠️ A second click is a second bill, so in-flight disables it; so does having no thumb. */
export function canEnrich(a: { lifecycle: string; thumbState: string; deleted: boolean; running: boolean }): boolean {
  return !a.running && !a.deleted && a.lifecycle === 'ready' && a.thumbState !== 'none';
}

export const SCAN_LABEL = 'Looking at the image…';
export const PENDING_BANNER = 'AI suggestion — not saved yet. Edit anything you like, then accept.';
